import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './config';
import socket from '../socket/socket';
import GameMatch from './GameMatch';
import AdminSidebar from './AdminSidebar';
import '../css/AdminMonitor.css';

const RANK_ORDER = ['S/P', 'S', 'N/S', 'N', 'N-', 'NB+', 'NB', 'BGM', 'BG', '-'];

const STATUS_LABELS = {
  ACTIVE: 'พร้อมลง',
  QUEUE: 'อยู่ในคิว',
  PLAY: 'กำลังเล่น',
  CHECKOUT: 'Check-out'
};

const toArray = value => (
  Array.isArray(value)
    ? value
    : Array.isArray(value?.data)
      ? value.data
      : []
);

const parseTimestamp = value => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const getArrivalTimestamp = player =>
  parseTimestamp(player?.checkin_time);

const getWaitingTimestamp = player =>
  parseTimestamp(
    player?.last_finished_at ||
    player?.active_since ||
    player?.checkin_time
  );

const getStatus = player =>
  String(player?.status || player?.memberStatus || '')
    .trim()
    .toUpperCase();

const getRank = player => {
  const rank = String(
    player?.level_rank || player?.rank || player?.hand_level || '-'
  )
    .replace(/^มือ\s*/i, '')
    .trim()
    .toUpperCase();

  return rank || '-';
};

const getTotalMatches = player =>
  Math.max(0, Number(player?.totalMatches || player?.total_matches || 0));

const formatClock = timestamp => {
  if (!Number.isFinite(timestamp)) return 'ไม่พบเวลา';

  return new Intl.DateTimeFormat('th-TH', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(timestamp));
};

const formatDuration = milliseconds => {
  if (!Number.isFinite(milliseconds)) return '-';

  const totalMinutes = Math.max(0, Math.floor(milliseconds / 60000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days} วัน ${hours} ชม.`;
  if (hours > 0) return `${hours} ชม. ${minutes} นาที`;
  return `${minutes} นาที`;
};

const compareName = (left, right) =>
  String(left?.name || '').localeCompare(String(right?.name || ''), 'th');

const fetchJson = async path => {
  const response = await fetch(`${API_BASE}${path}`);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || data.error || `HTTP ${response.status}`);
  }

  return data;
};

function PlayerColumn({
  title,
  subtitle,
  icon,
  tone,
  players,
  renderMetric,
  onEdit,
  emptyText = 'ยังไม่มีสมาชิกใน Group นี้'
}) {
  return (
    <section className={`monitor-list-card tone-${tone}`}>
      <header className="monitor-list-header">
        <div className="monitor-list-title">
          <span className="monitor-list-icon" aria-hidden="true">{icon}</span>
          <span>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </span>
        </div>
        <strong className="monitor-list-count">{players.length} คน</strong>
      </header>

      <div className="monitor-player-list">
        {players.length === 0 ? (
          <div className="monitor-empty">{emptyText}</div>
        ) : players.map((player, index) => {
          const status = getStatus(player) || '-';

          return (
            <article className="monitor-player-row" key={player.user_id || `${player.name}-${index}`}>
              <span className="monitor-order">{index + 1}</span>

              <div className="monitor-player-main">
                <strong>{player.name || 'ไม่ระบุชื่อ'}</strong>
                <div className="monitor-player-tags">
                  <span className="monitor-rank">มือ {getRank(player)}</span>
                  <span className={`monitor-status status-${status.toLowerCase()}`}>
                    {STATUS_LABELS[status] || status}
                  </span>
                  <span className="monitor-point">{Number(player.point || 0)} คะแนน</span>
                </div>
              </div>

              <div className="monitor-player-side">
                <div className="monitor-player-metric">
                  {renderMetric(player)}
                </div>
                <button
                  type="button"
                  className="monitor-player-edit-button"
                  title={`แก้ไขข้อมูล ${player.name || 'ผู้เล่น'}`}
                  onClick={() => onEdit(player)}
                >
                  ✏️ Edit
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function AdminMonitor() {
  const { groupId: routeGroupId } = useParams();
  const navigate = useNavigate();
  const selectedGroupId = Number(routeGroupId) || null;

  const [groups, setGroups] = useState([]);
  const [players, setPlayers] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [playerSearch, setPlayerSearch] = useState('');
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [savingPlayer, setSavingPlayer] = useState(false);
  const requestVersionRef = useRef(0);

  const loadGroups = useCallback(async () => {
    const data = await fetchJson('/api/groups');
    const nextGroups = toArray(data);
    setGroups(nextGroups);
    return nextGroups;
  }, []);

  const loadSelectedGroup = useCallback(async ({ silent = false } = {}) => {
    if (!selectedGroupId) {
      setPlayers([]);
      setCourts([]);
      setLoading(false);
      return;
    }

    const requestVersion = ++requestVersionRef.current;
    if (silent) setRefreshing(true);
    else setLoading(true);

    try {
      const [playerData, courtData] = await Promise.all([
        fetchJson(`/api/game-match/group/${selectedGroupId}/players`),
        fetchJson(`/api/courts/${selectedGroupId}`)
      ]);

      if (requestVersion !== requestVersionRef.current) return;

      setPlayers(toArray(playerData));
      setCourts(toArray(courtData));
      setError('');
      setLastUpdatedAt(Date.now());
      setNow(Date.now());
    } catch (loadError) {
      if (requestVersion !== requestVersionRef.current) return;
      setError(loadError.message || 'โหลดข้อมูล น้ามาด Monitor ไม่สำเร็จ');
    } finally {
      if (requestVersion === requestVersionRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [selectedGroupId]);

  useEffect(() => {
    loadGroups().catch(loadError => {
      setError(loadError.message || 'โหลดรายการ Group ไม่สำเร็จ');
      setLoading(false);
    });
  }, [loadGroups]);

  useEffect(() => {
    if (groups.length === 0) return;

    const selectedExists = groups.some(
      group => Number(group.id) === Number(selectedGroupId)
    );

    if (!selectedExists) {
      navigate(`/admin_monitor/${groups[0].id}`, { replace: true });
    }
  }, [groups, navigate, selectedGroupId]);

  useEffect(() => {
    loadSelectedGroup();
  }, [loadSelectedGroup]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(Date.now());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!selectedGroupId) return undefined;

    const syncSelectedGroup = () => {
      loadSelectedGroup({ silent: true });
    };
    const intervalId = window.setInterval(() => {
      syncSelectedGroup();
    }, 10000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncSelectedGroup();
    };

    window.addEventListener('focus', syncSelectedGroup);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncSelectedGroup);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadSelectedGroup, selectedGroupId]);

  useEffect(() => {
    if (!selectedGroupId) return undefined;

    const joinGroup = () => {
      socket.emit('join-group', Number(selectedGroupId));
      loadSelectedGroup({ silent: true });
    };
    const handlePlayerAdded = (payload = {}) => {
      const eventGroupId = payload.groupId ?? payload.group_id;
      if (Number(eventGroupId) !== Number(selectedGroupId)) return;

      const userId = Number(payload.user_id ?? payload.userId);
      if (Number.isInteger(userId) && userId > 0) {
        const realtimePlayer = {
          ...payload,
          user_id: userId,
          status: payload.status || payload.memberStatus || 'ACTIVE',
          memberStatus:
            payload.memberStatus || payload.status || 'ACTIVE',
          totalMatches: Number(
            payload.totalMatches || payload.total_matches || 0
          )
        };

        setPlayers(currentPlayers => {
          const existingIndex = currentPlayers.findIndex(
            player => Number(player.user_id) === userId
          );

          if (existingIndex < 0) {
            return [...currentPlayers, realtimePlayer];
          }

          return currentPlayers.map((player, index) => (
            index === existingIndex
              ? { ...player, ...realtimePlayer }
              : player
          ));
        });
        setNow(Date.now());
        setLastUpdatedAt(Date.now());
      }

      // ดึงจากฐานข้อมูลซ้ำเพื่อยืนยันค่าเวลา สไตล์ และจำนวนเกมล่าสุด
      loadSelectedGroup({ silent: true });
    };
    const refreshGroupData = (payload = {}) => {
      const eventGroupId = payload.groupId ?? payload.group_id;
      if (eventGroupId && Number(eventGroupId) !== Number(selectedGroupId)) return;
      loadSelectedGroup({ silent: true });
    };
    const refreshGroups = () => {
      loadGroups().catch(() => {});
    };
    const groupEvents = [
      'member-joined',
      'member-updated',
      'member-checkout',
      'player-updated',
      'point-updated',
      'player-status-updated',
      'player-style-updated',
      'match-generated',
      'match-started',
      'match-ended',
      'match-updated',
      'queue-updated',
      'courts-added',
      'court-status-updated',
      'court-deleted',
      'courts-initialized'
    ];
    const allGroupEvents = ['group-created', 'group-closed', 'group-deleted'];

    socket.on('connect', joinGroup);
    socket.on('player-added', handlePlayerAdded);
    groupEvents.forEach(eventName => socket.on(eventName, refreshGroupData));
    allGroupEvents.forEach(eventName => socket.on(eventName, refreshGroups));

    if (socket.connected) joinGroup();

    return () => {
      socket.off('connect', joinGroup);
      socket.off('player-added', handlePlayerAdded);
      groupEvents.forEach(eventName => socket.off(eventName, refreshGroupData));
      allGroupEvents.forEach(eventName => socket.off(eventName, refreshGroups));
    };
  }, [loadGroups, loadSelectedGroup, selectedGroupId]);

  const selectedGroup = useMemo(
    () => groups.find(group => Number(group.id) === Number(selectedGroupId)),
    [groups, selectedGroupId]
  );

  const arrivalPlayers = useMemo(
    () => [...players].sort((left, right) => {
      const leftTime = getArrivalTimestamp(left) ?? Number.MAX_SAFE_INTEGER;
      const rightTime = getArrivalTimestamp(right) ?? Number.MAX_SAFE_INTEGER;
      return leftTime - rightTime || compareName(left, right);
    }),
    [players]
  );

  const latestArrivalPlayers = useMemo(
    () => [...players].sort((left, right) => {
      const leftTime = getArrivalTimestamp(left) ?? Number.MIN_SAFE_INTEGER;
      const rightTime = getArrivalTimestamp(right) ?? Number.MIN_SAFE_INTEGER;

      return (
        rightTime - leftTime ||
        Number(right?.user_id || 0) - Number(left?.user_id || 0) ||
        compareName(left, right)
      );
    }),
    [players]
  );

  const waitingPlayers = useMemo(
    () => [...players].sort((left, right) => {
      const statusPriority = { ACTIVE: 0, QUEUE: 1, PLAY: 2 };
      const leftStatus = statusPriority[getStatus(left)] ?? 3;
      const rightStatus = statusPriority[getStatus(right)] ?? 3;
      const leftTime = getWaitingTimestamp(left) ?? Number.MAX_SAFE_INTEGER;
      const rightTime = getWaitingTimestamp(right) ?? Number.MAX_SAFE_INTEGER;

      return (
        leftStatus - rightStatus ||
        leftTime - rightTime ||
        compareName(left, right)
      );
    }),
    [players]
  );

  const fewestGamePlayers = useMemo(
    () => [...players].sort((left, right) => (
      getTotalMatches(left) - getTotalMatches(right) ||
      (getArrivalTimestamp(left) ?? Number.MAX_SAFE_INTEGER) -
        (getArrivalTimestamp(right) ?? Number.MAX_SAFE_INTEGER) ||
      compareName(left, right)
    )),
    [players]
  );

  const normalizedPlayerSearch = playerSearch.trim().toLocaleLowerCase('th');
  const filterPlayersBySearch = useCallback(list => {
    if (!normalizedPlayerSearch) return list;

    return list.filter(player => (
      [
        player?.name,
        getRank(player),
        player?.point,
        getStatus(player),
        STATUS_LABELS[getStatus(player)]
      ]
        .filter(value => value !== undefined && value !== null)
        .some(value => String(value).toLocaleLowerCase('th').includes(normalizedPlayerSearch))
    ));
  }, [normalizedPlayerSearch]);

  const visibleArrivalPlayers = useMemo(
    () => filterPlayersBySearch(arrivalPlayers),
    [arrivalPlayers, filterPlayersBySearch]
  );
  const visibleWaitingPlayers = useMemo(
    () => filterPlayersBySearch(waitingPlayers),
    [waitingPlayers, filterPlayersBySearch]
  );
  const visibleFewestGamePlayers = useMemo(
    () => filterPlayersBySearch(fewestGamePlayers),
    [fewestGamePlayers, filterPlayersBySearch]
  );

  const rankSummary = useMemo(() => {
    const counts = new Map(RANK_ORDER.map(rank => [rank, 0]));

    players.forEach(player => {
      const rank = getRank(player);
      counts.set(rank, Number(counts.get(rank) || 0) + 1);
    });

    const extraRanks = [...counts.keys()]
      .filter(rank => !RANK_ORDER.includes(rank))
      .sort((left, right) => left.localeCompare(right, 'th'));

    return [...RANK_ORDER, ...extraRanks].map(rank => ({
      rank,
      count: Number(counts.get(rank) || 0)
    }));
  }, [players]);

  const activeCount = players.filter(player => getStatus(player) === 'ACTIVE').length;
  const playingCount = players.filter(player => getStatus(player) === 'PLAY').length;
  const queueCount = players.filter(player => getStatus(player) === 'QUEUE').length;
  const openCourtCount = courts.filter(
    court => String(court.status || '').toUpperCase() === 'OPEN'
  ).length;
  const busyCourtCount = courts.filter(
    court => String(court.status || '').toUpperCase() === 'CHECK'
  ).length;

  const openPlayerEditor = player => {
    const currentStatus = ['ACTIVE', 'QUEUE', 'PLAY', 'CHECKOUT'].includes(
      getStatus(player)
    )
      ? getStatus(player)
      : 'ACTIVE';

    setEditingPlayer({
      user_id: Number(player.user_id),
      name: String(player.name || ''),
      level_rank: getRank(player),
      point: String(Number(player.point || 0)),
      status: currentStatus,
      original_status: currentStatus
    });
  };

  const closePlayerEditor = () => {
    if (savingPlayer) return;
    setEditingPlayer(null);
  };

  const savePlayerEdit = async event => {
    event.preventDefault();
    if (!editingPlayer || savingPlayer) return;

    const name = editingPlayer.name.trim();
    const point = Number(editingPlayer.point);
    const levelRank = String(editingPlayer.level_rank || '').trim().toUpperCase();

    if (!name) {
      alert('กรุณากรอกชื่อผู้เล่น');
      return;
    }

    if (!Number.isFinite(point) || point < 0) {
      alert('คะแนนต้องเป็นตัวเลขตั้งแต่ 0 ขึ้นไป');
      return;
    }

    try {
      setSavingPlayer(true);

      const response = await fetch(
        `${API_BASE}/api/users/${editingPlayer.user_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            level_rank: levelRank,
            point
          })
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.message || data.error || `อัปเดตผู้เล่นไม่สำเร็จ (HTTP ${response.status})`
        );
      }

      if (editingPlayer.status !== editingPlayer.original_status) {
        const statusResponse = await fetch(
          `${API_BASE}/api/users/${editingPlayer.user_id}/group/${selectedGroupId}/status`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: editingPlayer.status })
          }
        );
        const statusData = await statusResponse.json().catch(() => ({}));

        if (!statusResponse.ok) {
          throw new Error(
            statusData.message ||
            statusData.error ||
            `อัปเดตสถานะไม่สำเร็จ (HTTP ${statusResponse.status})`
          );
        }
      }

      setPlayers(currentPlayers => currentPlayers.map(player => (
        Number(player.user_id) === Number(editingPlayer.user_id)
          ? {
              ...player,
              name,
              level_rank: levelRank,
              point,
              status: editingPlayer.status,
              memberStatus: editingPlayer.status
            }
          : player
      )));
      setEditingPlayer(null);
      setLastUpdatedAt(Date.now());
      await loadSelectedGroup({ silent: true });
    } catch (saveError) {
      alert(`❌ ${saveError.message}`);
    } finally {
      setSavingPlayer(false);
    }
  };

  const handleGroupChange = event => {
    const nextGroupId = Number(event.target.value);
    if (!nextGroupId) return;
    setEditingPlayer(null);
    setPlayerSearch('');
    navigate(`/admin_monitor/${nextGroupId}`);
  };

  const openDashboardMenu = action => {
    const needsGroup = ['members', 'match', 'court', 'players'].includes(
      action
    );

    if (needsGroup && !selectedGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดเมนูนี้');
      return;
    }

    navigate('/dashboard', {
      state: {
        dashboardAction: action,
        selectedGroupId
      }
    });
  };

  const logout = () => {
    window.localStorage.clear();
    navigate('/');
  };

  return (
    <main className="monitor-page admin-shell-with-sidebar">
      <AdminSidebar active="monitor" groupId={selectedGroupId} />
      <header className="monitor-hero">
        <div className="monitor-brand">
          <span className="monitor-live-dot" aria-hidden="true" />
          <div>
            <span className="monitor-eyebrow">LIVE CLUB OVERVIEW</span>
            <h1>🖥️ น้ามาด Monitor</h1>
            <p>ภาพรวมสมาชิก คิวรอ จำนวนเกม ระดับมือ และคอร์ทแบบเรียลไทม์</p>
          </div>
        </div>

        <div className="monitor-toolbar">
          <label htmlFor="monitor-group-select">
            <span>เลือก Group</span>
            <select
              id="monitor-group-select"
              value={selectedGroupId || ''}
              onChange={handleGroupChange}
              disabled={groups.length === 0}
            >
              {groups.length === 0 && <option value="">ไม่พบ Group</option>}
              {groups.map(group => (
                <option value={group.id} key={group.id}>
                  {group.Group_Name} ({group.Group_Status || 'OPEN'})
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="monitor-refresh-button"
            disabled={!selectedGroupId || refreshing}
            onClick={() => loadSelectedGroup({ silent: true })}
          >
            {refreshing ? '⏳ กำลังอัปเดต' : '↻ อัปเดตข้อมูล'}
          </button>

          <button
            type="button"
            className="monitor-dashboard-button"
            onClick={() => navigate('/dashboard')}
          >
            ← Dashboard
          </button>
        </div>
      </header>

      <section className="monitor-summary-grid" aria-label="ข้อมูลสรุป">
        <article className="monitor-summary-card members">
          <span>สมาชิกทั้งหมด</span>
          <strong>{players.length}</strong>
          <small>ACTIVE {activeCount} • QUEUE {queueCount} • PLAY {playingCount}</small>
        </article>

        <article className="monitor-summary-card courts">
          <span>จำนวนคอร์ททั้งหมด</span>
          <strong>{courts.length}</strong>
          <small>ว่าง {openCourtCount} • ใช้งาน {busyCourtCount}</small>
        </article>

        <article className="monitor-summary-card group">
          <span>Group ที่กำลังดู</span>
          <strong>{selectedGroup?.Group_Name || '-'}</strong>
          <small>สถานะ {selectedGroup?.Group_Status || '-'}</small>
        </article>
      </section>

      <section className="monitor-rank-section">
        <div className="monitor-rank-heading">
          <span>
            <strong>ระดับมือของสมาชิกทั้งหมด</strong>
            <small>รวม {players.length} คน</small>
          </span>
          <span className="monitor-realtime-status">
            <i aria-hidden="true" /> REALTIME
          </span>
        </div>

        <div className="monitor-rank-list">
          {rankSummary.map(item => (
            <span className={`monitor-rank-chip ${item.count === 0 ? 'empty' : ''}`} key={item.rank}>
              <b>{item.rank}</b>
              <strong>{item.count}</strong>
            </span>
          ))}
        </div>
      </section>

      {error && (
        <div className="monitor-error" role="alert">
          ❌ {error}
        </div>
      )}

      <section className="monitor-player-search" aria-label="ค้นหาผู้เล่น">
        <div className="monitor-player-search-heading">
          <span aria-hidden="true">🔎</span>
          <span>
            <strong>ค้นหาและแก้ไขผู้เล่น</strong>
            <small>ค้นหาจากชื่อ ระดับมือ คะแนน หรือสถานะ แล้วกด Edit ที่รายชื่อ</small>
          </span>
        </div>
        <label>
          <span className="sr-only">ค้นหาผู้เล่น</span>
          <input
            type="search"
            value={playerSearch}
            placeholder="ค้นหาชื่อ ระดับมือ คะแนน หรือสถานะ..."
            onChange={event => setPlayerSearch(event.target.value)}
          />
        </label>
        <span className="monitor-player-search-result">
          พบ {visibleArrivalPlayers.length}/{players.length} คน
        </span>
        {playerSearch && (
          <button
            type="button"
            className="monitor-player-search-clear"
            onClick={() => setPlayerSearch('')}
          >
            ✕ ล้าง
          </button>
        )}
      </section>

      <section className="monitor-latest-arrivals" aria-label="สมาชิกที่เพิ่งมาถึงล่าสุด">
        <header className="monitor-latest-arrivals-header">
          <span className="monitor-latest-arrivals-icon" aria-hidden="true">✨</span>
          <span>
            <strong>สมาชิกที่เพิ่งมาถึงล่าสุด</strong>
            <small>เรียงจากคนที่มาถึงล่าสุดไปหาเก่าสุด</small>
          </span>
          <b>{latestArrivalPlayers.length} คน</b>
        </header>

        <div className="monitor-latest-arrivals-track">
          {latestArrivalPlayers.length === 0 ? (
            <div className="monitor-latest-arrivals-empty">
              ยังไม่มีสมาชิกใน Group นี้
            </div>
          ) : latestArrivalPlayers.map((player, index) => {
            const arrivedAt = getArrivalTimestamp(player);
            const status = getStatus(player) || '-';

            return (
              <button
                type="button"
                className="monitor-latest-arrival-card"
                key={player.user_id || `${player.name}-${index}`}
                title={`แก้ไขข้อมูล ${player.name || 'ผู้เล่น'}`}
                onClick={() => openPlayerEditor(player)}
              >
                <span className="monitor-latest-arrival-order">{index + 1}</span>
                <span className="monitor-latest-arrival-info">
                  <strong>{player.name || 'ไม่ระบุชื่อ'}</strong>
                  <small>
                    มือ {getRank(player)} · {STATUS_LABELS[status] || status}
                  </small>
                </span>
                <span className="monitor-latest-arrival-time">
                  <strong>{formatClock(arrivedAt)}</strong>
                  <small>
                    {Number.isFinite(arrivedAt)
                      ? `${formatDuration(now - arrivedAt)}ที่แล้ว`
                      : 'ไม่พบเวลา'}
                  </small>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {loading ? (
        <div className="monitor-loading">กำลังโหลดข้อมูล น้ามาด Monitor...</div>
      ) : (
        <section className="monitor-columns">
          <PlayerColumn
            icon="🚪"
            tone="arrival"
            title="สมาชิกที่มาก่อน"
            subtitle="รายชื่อทั้งหมด เรียงตามเวลา Check-in คนที่มาถึงก่อนอยู่บนสุด"
            players={visibleArrivalPlayers}
            onEdit={openPlayerEditor}
            emptyText={playerSearch ? 'ไม่พบผู้เล่นที่ค้นหา' : undefined}
            renderMetric={player => {
              const arrivedAt = getArrivalTimestamp(player);
              return (
                <>
                  <small>มาถึง</small>
                  <strong>{formatClock(arrivedAt)}</strong>
                  <span>
                    {Number.isFinite(arrivedAt)
                      ? formatDuration(now - arrivedAt)
                      : '-'}
                  </span>
                </>
              );
            }}
          />

          <PlayerColumn
            icon="⏱"
            tone="waiting"
            title="สมาชิกที่รอนาน"
            subtitle="รายชื่อทั้งหมด โดย ACTIVE ที่รอนานอยู่ก่อน ตามด้วย Queue และผู้เล่นในสนาม"
            players={visibleWaitingPlayers}
            onEdit={openPlayerEditor}
            emptyText={playerSearch ? 'ไม่พบผู้เล่นที่ค้นหา' : undefined}
            renderMetric={player => {
              const status = getStatus(player);
              const waitingAt = getWaitingTimestamp(player);
              const canCountWaiting = status === 'ACTIVE';

              return (
                <>
                  <small>{canCountWaiting ? 'เวลารอ' : 'สถานะ'}</small>
                  <strong>
                    {canCountWaiting
                      ? Number.isFinite(waitingAt)
                        ? formatDuration(now - waitingAt)
                        : '-'
                      : STATUS_LABELS[status] || status || '-'}
                  </strong>
                  <span>
                    {canCountWaiting
                      ? `เริ่ม ${formatClock(waitingAt)}`
                      : 'ไม่นับเวลารอ'}
                  </span>
                </>
              );
            }}
          />

          <PlayerColumn
            icon="🎮"
            tone="games"
            title="สมาชิกที่เล่นน้อยที่สุด"
            subtitle="รายชื่อทั้งหมด นับเฉพาะ Match ที่กำลังเล่นหรือเล่นจบแล้ว"
            players={visibleFewestGamePlayers}
            onEdit={openPlayerEditor}
            emptyText={playerSearch ? 'ไม่พบผู้เล่นที่ค้นหา' : undefined}
            renderMetric={player => (
              <>
                <small>จำนวนเกม</small>
                <strong>{getTotalMatches(player)} เกม</strong>
                <span>มาถึง {formatClock(getArrivalTimestamp(player))}</span>
              </>
            )}
          />
        </section>
      )}

      {selectedGroupId && (
        <section className="admin-monitor-match-board" aria-label="Match Queue และ Match Start">
          <GameMatch
            groupId={selectedGroupId}
            displayMode="monitor-board"
          />
        </section>
      )}

      {editingPlayer && createPortal(
        <div
          className="monitor-player-editor-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) closePlayerEditor();
          }}
        >
          <form
            className="monitor-player-editor"
            role="dialog"
            aria-modal="true"
            aria-labelledby="monitor-player-editor-title"
            onSubmit={savePlayerEdit}
          >
            <header className="monitor-player-editor-header">
              <span className="monitor-player-editor-icon" aria-hidden="true">✏️</span>
              <span>
                <small>PLAYER EDITOR</small>
                <h2 id="monitor-player-editor-title">แก้ไขข้อมูลผู้เล่น</h2>
                <p>Group: {selectedGroup?.Group_Name || selectedGroupId}</p>
              </span>
              <button
                type="button"
                className="monitor-player-editor-close"
                aria-label="ปิดหน้าต่างแก้ไขผู้เล่น"
                disabled={savingPlayer}
                onClick={closePlayerEditor}
              >
                ✕
              </button>
            </header>

            <div className="monitor-player-editor-fields">
              <label>
                <span>ชื่อผู้เล่น</span>
                <input
                  autoFocus
                  value={editingPlayer.name}
                  onChange={event => setEditingPlayer(current => ({
                    ...current,
                    name: event.target.value
                  }))}
                />
              </label>

              <label>
                <span>ระดับมือ</span>
                <select
                  value={editingPlayer.level_rank}
                  onChange={event => setEditingPlayer(current => ({
                    ...current,
                    level_rank: event.target.value
                  }))}
                >
                  {!RANK_ORDER.includes(editingPlayer.level_rank) && (
                    <option value={editingPlayer.level_rank}>{editingPlayer.level_rank}</option>
                  )}
                  {RANK_ORDER.map(rank => (
                    <option value={rank} key={rank}>{rank}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>คะแนน</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={editingPlayer.point}
                  onChange={event => setEditingPlayer(current => ({
                    ...current,
                    point: event.target.value
                  }))}
                />
              </label>

              <label>
                <span>สถานะใน Group</span>
                <select
                  value={editingPlayer.status}
                  onChange={event => setEditingPlayer(current => ({
                    ...current,
                    status: event.target.value
                  }))}
                >
                  <option value="ACTIVE">ACTIVE — พร้อมลง</option>
                  <option value="QUEUE">QUEUE — อยู่ในคิว</option>
                  <option value="PLAY">PLAY — กำลังเล่น</option>
                  <option value="CHECKOUT">CHECKOUT — ออกจากก๊วน</option>
                </select>
              </label>
            </div>

            <footer className="monitor-player-editor-actions">
              <button
                type="button"
                className="secondary"
                disabled={savingPlayer}
                onClick={closePlayerEditor}
              >
                ยกเลิก
              </button>
              <button type="submit" className="primary" disabled={savingPlayer}>
                {savingPlayer ? '⏳ กำลังบันทึก...' : '💾 บันทึกข้อมูล'}
              </button>
            </footer>
          </form>
        </div>,
        document.body
      )}

      <nav className="admin-monitor-bottom-nav" aria-label="เมนูผู้ดูแลระบบ">
        <button
          type="button"
          title="จัดการ Groups"
          aria-label="เปิดเมนู Groups"
          onClick={() => openDashboardMenu('groups')}
        >
          📁
        </button>
        <button
          type="button"
          title="จัดการสมาชิก"
          aria-label="เปิดเมนูสมาชิก"
          onClick={() => openDashboardMenu('members')}
        >
          👥
        </button>
        <button
          type="button"
          title="จัด Match"
          aria-label="เปิดหน้าจัด Match"
          onClick={() => openDashboardMenu('match')}
        >
          🎲
        </button>
        <button
          type="button"
          title="จัดการ Court"
          aria-label="เปิดเมนู Court"
          onClick={() => openDashboardMenu('court')}
        >
          🏟
        </button>
        <button
          type="button"
          title="จัดการผู้เล่น"
          aria-label="เปิดเมนูผู้เล่น"
          onClick={() => openDashboardMenu('players')}
        >
          🧑
        </button>
        <button
          type="button"
          className="is-active"
          title="น้ามาด Monitor"
          aria-label="หน้า น้ามาด Monitor ปัจจุบัน"
          aria-current="page"
        >
          📺
        </button>
        <button
          type="button"
          title="ออกจากระบบ"
          aria-label="ออกจากระบบ"
          onClick={logout}
        >
          🚪
        </button>
      </nav>

      <footer className="monitor-footer">
        <span>Group ID: {selectedGroupId || '-'}</span>
        <span>
          อัปเดตล่าสุด {lastUpdatedAt ? formatClock(lastUpdatedAt) : '-'}
        </span>
      </footer>
    </main>
  );
}

export default AdminMonitor;
